import {hpcAttrs} from '@github-ui/hpc'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import {useRef, useEffect, useEffectEvent, useState, useCallback, createRef, type RefObject} from 'react'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {Link} from '@github-ui/react-router'
import {useLocalStorage} from '@github-ui/use-safe-storage/local-storage'
/* eslint-disable-next-line primer-react/no-deprecated-flash -- Existing Flash usage should be migrated to Banner. */
import {Flash, IconButton, ActionMenu, Button, Label} from '@primer/react'
import {KebabHorizontalIcon, NoEntryIcon} from '@primer/octicons-react'
import {FlashUpsellBanner, UpsellBanner} from '../components/UpsellBanner'
import {useRelativeNavigation} from '../hooks/use-relative-navigation'
import {useIsStafftools} from '../hooks/use-is-stafftools'
import {useRuleset} from '../hooks/use-ruleset'
import {
  type IncludeExcludeParameters,
  type RepositoryIdParameters,
  type OrganizationIdParameters,
  RulesetEnforcement,
  type RulesetRoutePayload,
  type ValidationError,
  type RulesetTarget,
  type Ruleset,
  type ParameterSchema,
  type ErrorRef,
  type TargetObjectType,
} from '@github-ui/repos-rules-types/rules-types'
import {getRuleset, setRuleset} from '../services/api'
import {GeneralPanel} from '../components/ruleset/GeneralPanel'
import {TargetsPanel} from '../components/ruleset/TargetsPanel'
import {RulesPanel} from '../components/ruleset/RulesPanel'
import {BypassListPanel} from '../components/ruleset/BypassListPanel'
import {DangerConfirmationDialog} from '../components/ruleset/DangerConfirmationDialog'
import sudo from '@github-ui/sudo'
import {RulesetActionMenu} from '../components/RulesetActionMenu'
import {IMPORT_LOCAL_STORAGE_KEY, TARGET_OBJECT_BY_TYPE} from '../helpers/constants'
import {isRulesetInherited} from '../helpers/utils'
import {PageHeader} from '@primer/react/experimental'
import {enforcementLabelText} from '../helpers/enforcement-label'
import {DismissibleFlashOrToast, type FlashAlert} from '@github-ui/dismissible-flash'
import {useTargetCount} from '../hooks/use-target-count'
import {capitalize, humanize} from '../helpers/string'
import {useRuleFeatures} from '../hooks/use-rule-features'
import {useRuleStrings} from '../hooks/use-rule-strings'
import type {Enterprise} from '@github-ui/repos-types'

import styles from './RulesetPage.module.css'
import {useUpsellInfoContext} from '../contexts/UpsellInfoContext'

const EMPTY_CONDITION_TARGET_OBJECTS: TargetObjectType[] = []

export function RulesetPage() {
  const isStafftools = useIsStafftools()
  const {
    source,
    sourceType,
    currentName,
    ruleset: initialRuleset,
    ruleSchemas: initialRuleSchemas,
    readOnly = false,
    isHistoryView = false,
    supportedConditionTargetObjects,
    optionalConditionTargetObjects = EMPTY_CONDITION_TARGET_OBJECTS,
    helpUrls,
    isImportedRuleset,
    isRestoredRuleset,
    noRulesets,
    initialErrors,
    additionalFilterProviders,
    showPropertiesBanner,
  } = useRoutePayload<RulesetRoutePayload>()
  const upsellInfo = useUpsellInfoContext()
  const [importedRuleset, setImportedRuleset] = useLocalStorage<Ruleset | undefined>(
    IMPORT_LOCAL_STORAGE_KEY,
    undefined,
  )
  // Snapshot the imported ruleset from local storage once on mount. An effect below clears
  // local storage, so we capture the initial value in state to keep it stable across renders.
  const [initialImportedRuleset] = useState(importedRuleset)

  const {importExportEnabled, targetDefinitions, publicPushRulesets} = useRuleFeatures()
  const {rulesetOrPolicy, upsellHeaderText} = useRuleStrings()

  const rulesImportExport = importExportEnabled

  const {
    rulesetId,
    rulesetTarget,
    rulesetName,
    rulesetNameError,
    dirtyRulesetName,
    enforcement,
    dirtyEnforcement,
    rules,
    ruleSchemas,
    dirtyRules,
    conditions,
    dirtyConditions,
    bypassActors,
    setBypassActors,
    dirtyBypassActors,
    initializeRuleset,
    addRule,
    removeRule,
    restoreRule,
    setRuleModalState,
    updateRuleParameters,
    addOrUpdateCondition,
    removeCondition,
    renameRuleset,
    setRulesetEnforcement,
    addBypassActor,
    removeBypassActor,
    updateBypassActor,
    validateForm,
  } = useRuleset(initialImportedRuleset || initialRuleset, initialRuleSchemas)

  const [serverRulesets, setServerRulesets] = useState([initialRuleset])

  const {rulesetPreviewCounts, rulesetPreviewSamples, rulesetPreviewErrors, rulesetPreviewUrls} = useTargetCount(
    source,
    sourceType,
    serverRulesets,
  )

  const {navigate, resolvePath} = useRelativeNavigation()

  const formRef = useRef<HTMLFormElement>(null)
  const rulesetNameRef = useRef<HTMLInputElement>(null)

  const [isSaving, setIsSaving] = useState(false)
  const [isReverting, setIsReverting] = useState(false)
  const [workflowTargetResetKey, setWorkflowTargetResetKey] = useState(0)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [rulesetToExport, setRulesetToExport] = useState(initialRuleset)
  const [flashAlert, setFlashAlert] = useState<FlashAlert>({message: '', variant: 'default'})
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [ruleErrors, setRuleErrors] = useState<Record<string, ValidationError[]>>(initialErrors?.rules || {})
  const [generalErrors, setGeneralErrors] = useState<Record<string, ValidationError[]>>(initialErrors?.general || {})
  const [conditionErrors, setConditionErrors] = useState<Record<string, ValidationError[]>>(
    initialErrors?.conditions || {},
  )
  const [errorRefs, setErrorRefs] = useState<ErrorRef>({})
  const repositoryConditionRef = useRef<HTMLButtonElement | null>(null)
  const refConditionRef = useRef<HTMLButtonElement | null>(null)
  const workflowConditionRef = useRef<HTMLButtonElement | null>(null)
  const flashRef = useRef<HTMLDivElement | null>(null)
  const importFlashRef = useRef<HTMLDivElement | null>(null)
  const hasImportedRuleset = isImportedRuleset && (!rulesImportExport || initialImportedRuleset)
  const bypassSupported =
    targetDefinitions[rulesetTarget] === undefined ? true : targetDefinitions[rulesetTarget].supportsBypass

  useEffect(() => {
    setImportedRuleset(undefined)
  }, [setImportedRuleset])

  // Focus on the first field on initial render
  useEffect(() => {
    rulesetNameRef?.current?.focus()
  }, [])

  useEffect(() => {
    flashRef.current?.focus()
  }, [flashAlert, flashRef])

  useEffect(() => {
    if (hasImportedRuleset) {
      importFlashRef.current?.focus()
    }
  }, [hasImportedRuleset])

  const createRefsForRuleSchema = useCallback((parameterSchema: ParameterSchema) => {
    const refs: Record<string, RefObject<HTMLElement | null>> = {}
    for (const field of parameterSchema.fields) {
      // eslint-disable-next-line @eslint-react/no-create-ref
      refs[field.name] = createRef<HTMLElement>()
    }
    return refs
  }, [])

  // Set the refs for all the possible rule error fields
  useEffect(() => {
    const refs: ErrorRef = {}
    for (const ruleSchema of ruleSchemas) {
      refs[ruleSchema.type] = {
        // Ref for the base rule
        // eslint-disable-next-line @eslint-react/no-create-ref
        errorRef: createRef<HTMLDivElement>(),
        // Refs for each rule field
        fields: createRefsForRuleSchema(ruleSchema.parameterSchema),
      }
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setErrorRefs(refs)
  }, [ruleSchemas, createRefsForRuleSchema])

  function resetErrors() {
    setRuleErrors({})
    setConditionErrors({})
    setGeneralErrors({})
    setErrorMessage(null)
  }

  const isFormDirty =
    dirtyRules.length > 0 ||
    dirtyConditions.length > 0 ||
    dirtyBypassActors.length > 0 ||
    dirtyRulesetName !== rulesetName ||
    dirtyEnforcement !== enforcement ||
    hasImportedRuleset

  const isInherited = isRulesetInherited({type: sourceType, id: source.id}, initialRuleset)

  const dirtyMessage = 'You have unsaved changes. Are you sure you want to leave this page?'

  // Parse the errors to determine the first to focus on
  // If there is no best field, we fallback to the form flash banner
  // error is the error from the catch block when attempting to save the ruleset
  function focusOnFirstError() {
    if (
      errorMessage ||
      Object.keys(generalErrors).length ||
      Object.keys(ruleErrors).length ||
      Object.keys(conditionErrors).length
    ) {
      // General errors
      // The only general field that can have an error is the name field
      if (generalErrors.name && generalErrors.name?.length > 0) {
        rulesetNameRef.current?.focus()
        return
      }

      if (Object.keys(conditionErrors).length > 0) {
        // Repository is first
        if (conditionErrors.repository && repositoryConditionRef.current) {
          repositoryConditionRef.current.focus()
          return
        }
        // Refs are after
        if (conditionErrors.ref && refConditionRef.current) {
          refConditionRef.current.focus()
          return
        }
        // Workflows are last
        if (conditionErrors.workflow && workflowConditionRef.current) {
          workflowConditionRef.current.focus()
          return
        }
      }

      // Rule errors
      if (ruleErrors) {
        // find the first rule error where we have a rule ref
        const firstRuleErrorType = Object.keys(ruleErrors).find(ruleType => errorRefs[ruleType])
        if (firstRuleErrorType) {
          // Focus on the rule-level error if present
          if (errorRefs[firstRuleErrorType]?.errorRef.current) {
            errorRefs[firstRuleErrorType]?.errorRef?.current?.focus()
            return
          }
          // Check if we can focus on a specific field
          const fields = firstRuleErrorType ? errorRefs[firstRuleErrorType]?.fields : null
          if (fields) {
            // We know the key exists because it's how we got `firstRuleErrorType`, so we can safely use `!`
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            for (const ruleError of ruleErrors[firstRuleErrorType]!) {
              if (ruleError.field && fields[ruleError.field]?.current) {
                fields[ruleError.field]?.current?.focus()
                return
              }
            }
          }
        }
      }

      // If we get here, then we couldn't find someting to focus on, so use the flash banner
      setFlashAlert({message: errorMessage || `Failed to save ${rulesetOrPolicy}`, variant: 'danger'})
    }
  }

  const onErrorsChanged = useEffectEvent(() => {
    focusOnFirstError()
  })

  useEffect(() => {
    // Any time the errors change, focus on the first error
    // eslint-disable-next-line react-hooks/set-state-in-effect
    onErrorsChanged()
  }, [ruleErrors, generalErrors, conditionErrors, errorMessage])

  const noTargetsHelper = (conditionTargetObject: TargetObjectType) => {
    if (optionalConditionTargetObjects.includes(conditionTargetObject)) {
      return false
    }

    const supportsEmuTargeting = sourceType === 'enterprise' && (source as Enterprise).enterpriseManaged === true
    const targetedCondition = conditions.find(
      condition => TARGET_OBJECT_BY_TYPE[condition.target] === conditionTargetObject,
    )
    if (targetedCondition) {
      if (targetedCondition.target === 'repository_id') {
        return (targetedCondition.parameters as RepositoryIdParameters).repository_ids.length === 0
      } else if (targetedCondition.target === 'organization_id') {
        const organizationParams = targetedCondition.parameters as OrganizationIdParameters
        const targetsEmuAccounts = supportsEmuTargeting && organizationParams.include_emu_accounts === true
        return organizationParams.organization_ids.length === 0 && !targetsEmuAccounts
      } else {
        const includeExcludeParams = targetedCondition.parameters as IncludeExcludeParameters
        const targetsEmuAccounts =
          supportsEmuTargeting &&
          targetedCondition.target === 'organization_name' &&
          includeExcludeParams.include_emu_accounts === true
        return (
          includeExcludeParams.include.length === 0 && includeExcludeParams.exclude.length === 0 && !targetsEmuAccounts
        )
      }
    } else return true
  }

  const noTargets = () => {
    return (
      supportedConditionTargetObjects.length > 0 &&
      supportedConditionTargetObjects.some(targetObject => noTargetsHelper(targetObject))
    )
  }

  useEffect(() => {
    document.body.setAttribute('data-turbo', 'false')
    window.onbeforeunload = isFormDirty
      ? e => {
          e.returnValue = dirtyMessage
        }
      : null

    return () => {
      document.body.removeAttribute('data-turbo')
      window.onbeforeunload = null
    }
  }, [dirtyMessage, isFormDirty])

  const onGoBack = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()

    if (isFormDirty && !window.confirm(dirtyMessage)) {
      return
    }

    navigate('..')
    window.onbeforeunload = null
  }

  const onRevertForm = async () => {
    if (isReverting || isSaving) {
      return
    }

    if (!isFormDirty) {
      setFlashAlert({message: 'No changes have been made', variant: 'default'})
      return
    }

    setIsReverting(true)

    try {
      const response = await getRuleset(resolvePath(''))
      const ruleset = response.payload.ruleset
      const updatedRuleSchemas = response.payload.ruleSchemas

      initializeRuleset({
        ruleset,
        source,
        sourceType,
        upsellInfo,
        ruleSchemas: updatedRuleSchemas,
        baseAvatarUrl: response.payload.baseAvatarUrl,
        supportedConditionTargetObjects: response.payload.supportedConditionTargetObjects,
        optionalConditionTargetObjects: response.payload.optionalConditionTargetObjects,
        helpUrls: response.payload.helpUrls,
      })
      setWorkflowTargetResetKey(current => current + 1)

      resetErrors()

      setFlashAlert({message: 'Changes reverted', variant: 'success'})
    } catch (e) {
      // @ts-expect-error catch blocks are bound to `unknown` so we need to validate the type before using it
      setFlashAlert({message: e.message || `Failed to get saved ${rulesetOrPolicy}`, variant: 'danger'})
    }
    setIsReverting(false)
  }

  async function canFormBeSaved() {
    const validationErrors = await validateForm()
    if (Object.values(validationErrors).some(x => x)) {
      // We currently only have client-side validation on the ruleset name
      // So we know if there are any errors, it must be from that field
      if (rulesetNameRef.current && !!validationErrors.name) {
        rulesetNameRef.current.focus()
      }
      return false
    }
    return true
  }

  const saveRuleset = async () => {
    if (isSaving || isReverting || !formRef.current) {
      return
    }

    setIsSaving(true)

    resetErrors()

    // We should reset the flash alert on save to not confuse subsequent requests with the previous one
    // since we may not always show a flash alert after save
    setFlashAlert({message: '', variant: 'default'})

    if (!(await sudo())) {
      setFlashAlert({message: 'Unauthorized', variant: 'danger'})
      setIsSaving(false)
      return
    }

    if (!(await canFormBeSaved())) {
      setIsSaving(false)
      return
    }

    try {
      const updatedRuleset = {
        ...initialRuleset,
        name: dirtyRulesetName,
        enforcement: dirtyEnforcement,
        rules,
        conditions,
        bypassActors,
      }

      const response = await setRuleset(resolvePath(''), updatedRuleset)
      setRulesetToExport(response.ruleset)
      initializeRuleset(response, {preserveWorkflowConditionMetadata: true})
      setServerRulesets([response.ruleset])
      setImportedRuleset(undefined)

      if (!updatedRuleset.id) {
        navigate(`../${response.ruleset.id}`, undefined, true, true)
        setFlashAlert({
          message: hasImportedRuleset
            ? `${capitalize(rulesetOrPolicy)} imported`
            : `${capitalize(rulesetOrPolicy)} created`,
          variant: 'success',
        })
      } else if (isRestoredRuleset) {
        navigate('', undefined, true, true)
        setFlashAlert({message: `${capitalize(rulesetOrPolicy)} restored`, variant: 'success'})
      } else {
        setFlashAlert({message: `${capitalize(rulesetOrPolicy)} updated`, variant: 'success'})
      }
    } catch (e) {
      // @ts-expect-error catch blocks are bound to `unknown` so we need to validate the type before using it
      if (e.message === 'No changes have been made') {
        // @ts-expect-error catch blocks are bound to `unknown` so we need to validate the type before using it
        setFlashAlert({message: e.message, variant: 'default'})
      } else {
        // @ts-expect-error catch blocks are bound to `unknown` so we need to validate the type before using it
        if (e.details) {
          // @ts-expect-error catch blocks are bound to `unknown` so we need to validate the type before using it
          setRuleErrors(e.details.rules as Record<string, ValidationError[]>)
          // @ts-expect-error catch blocks are bound to `unknown` so we need to validate the type before using it
          setConditionErrors(e.details.conditions as Record<string, ValidationError[]>)
          // @ts-expect-error catch blocks are bound to `unknown` so we need to validate the type before using it
          setGeneralErrors(e.details.general as Record<string, ValidationError[]>)
          // @ts-expect-error catch blocks are bound to `unknown` so we need to validate the type before using it
          setErrorMessage(e.message)
        }
      }
    } finally {
      setIsSaving(false)
    }
  }

  const deleteRuleset = async () => {
    setShowDeleteDialog(false)
    if (!(await sudo())) {
      setFlashAlert({
        variant: 'danger',
        message: 'Unauthorized',
      })
      return
    }

    try {
      const res = await verifiedFetch(resolvePath(''), {
        method: 'delete',
      })
      if (res.ok) {
        navigate('..', undefined, true, true)
      } else {
        setShowDeleteDialog(false)
        setFlashAlert({
          variant: 'danger',
          message: `Error deleting ${rulesetOrPolicy}`,
        })
      }
    } catch {
      setShowDeleteDialog(false)
      setFlashAlert({
        variant: 'danger',
        message: `Error deleting ${rulesetOrPolicy}`,
      })
    }
  }

  return (
    /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
    <div className="settings-next position-relative">
      <PageHeader className={styles.RulesetPageHeader}>
        <RulesHeader
          rulesetTarget={rulesetTarget}
          resolvePath={resolvePath}
          onGoBack={onGoBack}
          isHistoryView={isHistoryView}
          currentName={currentName}
          rulesetId={rulesetId}
          rulesetName={rulesetName}
          readOnly={readOnly}
          isStafftools={isStafftools}
          isInherited={isInherited}
          rulesetToExport={rulesetToExport}
          isRestoredRuleset={isRestoredRuleset}
          enforcement={enforcement}
          setFlashAlert={setFlashAlert}
          setShowDeleteDialog2={setShowDeleteDialog}
        />
      </PageHeader>
      <hr />
      <form
        method="post"
        action={resolvePath('')}
        ref={formRef}
        noValidate
        onSubmit={async e => {
          e.preventDefault()
          saveRuleset()
        }}
      >
        {noRulesets ? (
          <UpsellBanner
            headingText={upsellHeaderText}
            withBorder
            showNewRulesetButton={false}
            infoVariant="one"
            sourceType={sourceType}
            ctaType="rulesets"
            setFlashAlert={setFlashAlert}
          />
        ) : (
          upsellInfo?.rulesets.cta.visible && <FlashUpsellBanner ctaType="rulesets" sourceType={sourceType} />
        )}
        <DismissibleFlashOrToast flashAlert={flashAlert} setFlashAlert={setFlashAlert} ref={flashRef} />

        {isRestoredRuleset && upsellInfo?.enterpriseRulesets.featureEnabled && (
          <div className="mb-2">
            <Flash variant="default" className="d-flex flex-items-center">
              <span>
                <span className="d-inline">
                  This {rulesetOrPolicy} has been restored to a previous version but the changes have not been saved.
                </span>
                <span className="text-bold d-inline"> Review and save the changes below. </span>
              </span>
            </Flash>
          </div>
        )}

        {isHistoryView && (
          <div className="mb-2">
            <Flash variant="warning" className="d-flex flex-items-center">
              <span>
                <span className="d-inline">
                  You are viewing a previous version of this {rulesetOrPolicy}. This {rulesetOrPolicy} has been modified
                  since this version. See the{' '}
                </span>
                <Link reloadDocument to={resolvePath('../../..')}>
                  latest version
                </Link>
                <span>.</span>
              </span>
            </Flash>
          </div>
        )}

        {rulesetTarget === 'push' && !rulesetId && sourceType === 'repository' && (
          <div className="mb-2">
            <Flash variant="default" className="d-flex flex-items-center">
              <span>
                <span className="d-inline">
                  Push rulesets are applied to a repository and its downstream repositories. To review the downstream
                  repositories that will inherit this {rulesetOrPolicy},{' '}
                  <Link to={resolvePath('../../../forks')}>visit the forks page</Link>.
                </span>
              </span>
            </Flash>
          </div>
        )}

        {hasImportedRuleset && (
          <div className="mb-2">
            <Flash variant="default" ref={importFlashRef} tabIndex={0} className="d-flex flex-items-center">
              <span>
                <span className="d-inline">
                  This {rulesetOrPolicy} has been imported but the changes have not been saved.
                </span>
                <span className="text-bold d-inline"> Review and save the changes below. </span>
              </span>
            </Flash>
          </div>
        )}

        {!upsellInfo.rulesets.cta.visible && noTargets() && rulesetId ? (
          <div className="mb-2">
            <Flash variant="warning" className="d-flex flex-items-center">
              <NoEntryIcon />
              This {rulesetOrPolicy} does not target any resources and will not be applied.
            </Flash>
          </div>
        ) : null}

        <div className="d-flex flex-column gap-4" {...hpcAttrs()}>
          {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
          <div className={`mt-${readOnly ? 0 : 2}`}>
            <GeneralPanel
              readOnly={readOnly}
              sourceType={sourceType}
              name={dirtyRulesetName}
              enforcement={dirtyEnforcement}
              rulesetId={rulesetId}
              renameRuleset={renameRuleset}
              rulesetNameError={rulesetNameError}
              setRulesetEnforcement={setRulesetEnforcement}
              generalErrors={generalErrors}
              nameRef={rulesetNameRef}
              rulesetTarget={rulesetTarget}
            />
          </div>

          {(!readOnly || isStafftools || isHistoryView) && bypassSupported && (
            <BypassListPanel
              readOnly={readOnly}
              bypassActors={bypassActors}
              setBypassActors={setBypassActors}
              addBypassActor={addBypassActor}
              removeBypassActor={removeBypassActor}
              updateBypassActor={updateBypassActor}
              rulesetTarget={rulesetTarget}
            />
          )}

          {supportedConditionTargetObjects.length > 0 && (
            <TargetsPanel
              rulesetId={rulesetId}
              readOnly={readOnly}
              rulesetTarget={rulesetTarget}
              fnmatchHelpUrl={helpUrls?.fnmatch}
              rulesetPreviewCount={rulesetPreviewCounts[initialRuleset.id]}
              rulesetPreviewSamples={rulesetPreviewSamples[initialRuleset.id]}
              rulesetError={rulesetPreviewErrors[initialRuleset.id]}
              rulesetPreviewUrl={rulesetPreviewUrls[initialRuleset.id]}
              dirtyConditions={dirtyConditions}
              supportedConditionTargetObjects={supportedConditionTargetObjects}
              optionalConditionTargetObjects={optionalConditionTargetObjects}
              conditions={conditions}
              conditionErrors={conditionErrors}
              repositoryConditionRef={repositoryConditionRef}
              refConditionRef={refConditionRef}
              workflowConditionRef={workflowConditionRef}
              workflowTargetResetKey={workflowTargetResetKey}
              source={source}
              sourceType={sourceType}
              additionalFilterProviders={additionalFilterProviders}
              showPropertiesBanner={showPropertiesBanner}
              publicPushRulesets={publicPushRulesets || false}
              {...{addOrUpdateCondition, removeCondition}}
            />
          )}

          <RulesPanel
            readOnly={readOnly}
            helpUrls={helpUrls}
            rulesetId={rulesetId}
            rulesetTarget={rulesetTarget}
            sourceType={sourceType}
            rules={rules}
            ruleSchemas={ruleSchemas}
            ruleErrors={ruleErrors}
            errorRefs={errorRefs}
            {...{addRule, removeRule, restoreRule, setRuleModalState, updateRuleParameters}}
          />
        </div>

        {!readOnly && (
          <div className={`d-flex gap-2 tmp-mt-4`} data-testid="ruleset-buttons">
            <Button
              aria-label={rulesetId ? (isSaving ? 'Saving...' : 'Save changes') : 'Create'}
              variant="primary"
              type="submit"
              disabled={isSaving || isReverting}
            >
              {rulesetId ? (
                isSaving ? (
                  <>Saving...</>
                ) : (
                  <>
                    Save<span className="hide-md">&nbsp;changes</span>
                  </>
                )
              ) : (
                'Create'
              )}
            </Button>
            {rulesetId ? (
              <Button
                aria-label={isReverting ? 'Reverting...' : 'Revert changes'}
                variant="default"
                type="button"
                onClick={onRevertForm}
                disabled={isReverting || isSaving}
              >
                {isReverting ? (
                  <>Reverting...</>
                ) : (
                  <>
                    Revert<span className="hide-md hide-sm">&nbsp;changes</span>
                  </>
                )}
              </Button>
            ) : null}
          </div>
        )}
      </form>
      <DangerConfirmationDialog
        isOpen={showDeleteDialog}
        title={`Delete ${rulesetOrPolicy}?`}
        text={`Are you sure you want to delete this ${rulesetOrPolicy}? This action cannot be undone.`}
        buttonText="Delete"
        onDismiss={() => {
          setShowDeleteDialog(false)
        }}
        onConfirm={deleteRuleset}
      />
    </div>
  )
}

function RulesHeader({
  rulesetTarget,
  resolvePath,
  onGoBack,
  isHistoryView,
  currentName,
  rulesetId,
  rulesetName,
  readOnly,
  isStafftools,
  isInherited,
  rulesetToExport,
  isRestoredRuleset,
  enforcement,
  setFlashAlert,
  setShowDeleteDialog2,
}: {
  rulesetTarget: RulesetTarget
  resolvePath: (pathToResolve: string) => string
  onGoBack: (e: React.MouseEvent<HTMLElement>) => void
  isHistoryView: boolean
  currentName?: string
  rulesetId?: number
  rulesetName: string
  readOnly: boolean
  isStafftools: boolean
  isInherited: boolean
  rulesetToExport: Ruleset
  isRestoredRuleset?: boolean
  enforcement: RulesetEnforcement
  setFlashAlert: (flashAlert: FlashAlert) => void
  setShowDeleteDialog2: (showDeleteDialog: boolean) => void
}) {
  const {historyEnabled, importExportEnabled, listViewEnabled} = useRuleFeatures()
  const upsellInfo = useUpsellInfoContext()
  const rulesHistory = historyEnabled
  const rulesImportExport = importExportEnabled
  function enforcementLabelVariant(enforcementLabel: RulesetEnforcement) {
    switch (enforcementLabel) {
      case RulesetEnforcement.Enabled:
        return 'success'
      case RulesetEnforcement.Evaluate:
        return 'severe'
      case RulesetEnforcement.Disabled:
        return 'secondary'
    }
  }
  const [menuOpen, setMenuOpen] = useState(false)

  const {heading, rulesetOrPolicy} = useRuleStrings()

  return (
    <>
      {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
      <div className="d-flex gap-1 flex-items-center f3 h1-override-shared-component">
        {listViewEnabled ? (
          <Link to={resolvePath('..')} onClick={onGoBack}>
            {heading}
          </Link>
        ) : (
          <span>{heading}</span>
        )}
        <span>/</span>
        {isHistoryView ? (
          <Link reloadDocument to={resolvePath('../../..')}>
            {currentName}
          </Link>
        ) : (
          <span>{rulesetId ? currentName || rulesetName : `New ${humanize(rulesetTarget)} ${rulesetOrPolicy}`}</span>
        )}
        {isHistoryView && (
          <>
            <span>/</span>
            <span>History</span>
          </>
        )}
        {!upsellInfo.rulesets.cta.visible && !isHistoryView && rulesetId ? (
          <Label variant={enforcementLabelVariant(enforcement)} className={styles.RulesetLabel}>
            {enforcementLabelText(enforcement)}
          </Label>
        ) : null}
      </div>
      <div className={styles.RulesetContentBox}>
        {rulesetId && (!readOnly || (isStafftools && !isInherited)) ? (
          <ActionMenu open={menuOpen} onOpenChange={() => setMenuOpen(!menuOpen)}>
            <ActionMenu.Anchor>
              <IconButton icon={KebabHorizontalIcon} aria-label="Open additional options" />
            </ActionMenu.Anchor>

            <ActionMenu.Overlay>
              <RulesetActionMenu
                ruleset={rulesetToExport}
                rulesetsUrl={resolvePath('..')}
                showDeleteAction
                readOnly={readOnly}
                rulesHistory={rulesHistory}
                isRestored={isRestoredRuleset}
                rulesImportExport={rulesImportExport}
                setFlashAlert={setFlashAlert}
                setMenuOpen={setMenuOpen}
                setShowDeleteDialog2={setShowDeleteDialog2}
              />
            </ActionMenu.Overlay>
          </ActionMenu>
        ) : null}
      </div>
    </>
  )
}
